import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import M_ChatCapsuleSuggestion from '@/components/M_Molecules/M_ChatCapsuleSuggestion/M_ChatCapsuleSuggestion';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { ChatCapsulePreview } from '@/services/chatApi';
import { useCapsuleSuggestions } from '@/hooks/useChat';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { SlashCommand, detectSlashCommand } from '@/utils/chat';
import styles from './O_ChatComposer.module.css';

interface O_ChatComposerProps {
  onSend: (payload: {
    text: string;
    capsules: ChatCapsulePreview[];
  }) => void | Promise<void>;
  disabled?: boolean;
}

const MAX_ATTACHMENTS = 5;
const MAX_TEXTAREA_HEIGHT = 160;

export default function O_ChatComposer({
  onSend,
  disabled = false,
}: O_ChatComposerProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<ChatCapsulePreview[]>([]);
  const [slashCommand, setSlashCommand] = useState<SlashCommand | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { suggestions, isLoading } = useCapsuleSuggestions(
    slashCommand?.query ?? null
  );
  const isSuggestionsOpen = slashCommand !== null;

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // box-sizing: border-box, а scrollHeight рамку не учитывает — без неё поле
    // выходит на пару пикселей ниже текста и в нём появляется скроллбар.
    const borderHeight = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight + borderHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [text]);

  const syncSlashCommand = useCallback((element: HTMLTextAreaElement) => {
    setSlashCommand(
      detectSlashCommand(element.value, element.selectionStart ?? 0)
    );
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    syncSlashCommand(event.target);
  };

  const attachCapsule = useCallback(
    (capsule: ChatCapsulePreview) => {
      setAttachments(previous =>
        previous.some(item => item.id === capsule.id) ||
        previous.length >= MAX_ATTACHMENTS
          ? previous
          : [...previous, capsule]
      );

      if (slashCommand) {
        // Убираем саму команду из текста — в сообщение уходит карточка капсулы.
        const caret = textareaRef.current?.selectionStart ?? text.length;
        setText(text.slice(0, slashCommand.startIndex) + text.slice(caret));
      }
      setSlashCommand(null);
      textareaRef.current?.focus();
    },
    [slashCommand, text]
  );

  const removeAttachment = (capsuleId: string) => {
    setAttachments(previous => previous.filter(item => item.id !== capsuleId));
  };

  const submit = useCallback(() => {
    const trimmedText = text.trim();
    if (disabled || (!trimmedText && attachments.length === 0)) return;

    void onSend({ text: trimmedText, capsules: attachments });
    setText('');
    setAttachments([]);
    setSlashCommand(null);
  }, [attachments, disabled, onSend, text]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isSuggestionsOpen && suggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex(
          current => (current + step + suggestions.length) % suggestions.length
        );
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        attachCapsule(suggestions[activeIndex]);
        return;
      }
    }

    if (event.key === 'Escape' && isSuggestionsOpen) {
      event.preventDefault();
      setSlashCommand(null);
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className={styles.wrapper}>
      {isSuggestionsOpen && (
        <div className={styles.suggestions}>
          <span className={cn(styles.suggestions_caption, 'text-caption')}>
            {t('chat.composer.capsuleHint')}
          </span>
          {suggestions.map((capsule, index) => (
            <M_ChatCapsuleSuggestion
              key={capsule.id}
              capsule={capsule}
              isActive={index === activeIndex}
              onSelect={attachCapsule}
              onHover={() => setActiveIndex(index)}
            />
          ))}
          {suggestions.length === 0 && (
            <span className={cn(styles.empty, 'text-caption')}>
              {isLoading
                ? t('chat.composer.searching')
                : t('chat.composer.noCapsules')}
            </span>
          )}
        </div>
      )}

      {attachments.length > 0 && (
        <div className={styles.attachments}>
          {attachments.map(capsule => (
            <span key={capsule.id} className={cn(styles.chip, 'text-caption')}>
              <Q_Icon.CapsIcon width={16} height={16} fill='none' />
              <span className={styles.chip_title}>{capsule.title}</span>
              <button
                type='button'
                className={styles.chip_remove}
                onClick={() => removeAttachment(capsule.id)}
                aria-label={t('chat.composer.removeAttachment')}
              >
                <Q_Icon.CrossIcon width={12} height={12} fill='none' />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.input_row}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={event => syncSlashCommand(event.currentTarget)}
          onBlur={() => setSlashCommand(null)}
          placeholder={t('chat.composer.placeholder')}
          className={cn(styles.textarea, 'text-p-8')}
          rows={1}
          disabled={disabled}
        />
        <A_CircleButton
          variant='primary'
          size='large'
          icon={<Q_Icon.SendFilledIcon width={26} height={26} />}
          onClick={submit}
        />
      </div>
    </div>
  );
}
